import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AdminData } from '../../_models/admin';
import { HrOrganogramData, HrStaffData } from '../../_models/hr';
import { ApiService } from '../../_services/api.service';
import { ChecksService } from '../../_services/checks.service';
import { NotificationService } from '../../_services/notification.service';

@Component({
  selector: 'app-hr-staff-details',
  templateUrl: './staff-details.component.html',
  styleUrl: './staff-details.component.css'
})
export class HrStaffDetailsComponent implements OnInit {
  staff: HrStaffData;
  organogram: HrOrganogramData;
  currentUser: AdminData;
  loading: boolean = true;
  processing: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    public checkService: ChecksService,
    private notifyService: NotificationService,
  ) {
    this.checkService.checkSession();
  }

  ngOnInit(): void {
    this.currentUser = this.checkService.getAdminData();
    const staffId = Number(this.route.snapshot.paramMap.get('id') || 0);
    if (staffId < 1) {
      this.router.navigate(['/hr/employee-profiles-kin']);
      return;
    }

    this.getStaff(staffId);
    this.getOrganogram(staffId);
  }

  getStaff(staffId: number): void {
    this.loading = true;
    this.apiService.getOneHrStaff(staffId).subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.loading = false;
      if (data.success === true) {
        this.staff = data.data;
      } else {
        this.showApiError(data, 'Unable to load staff details');
      }
    });
  }

  getOrganogram(staffId: number): void {
    this.apiService.getHrOrganogramStaff(staffId).subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.organogram = data.data;
      } else {
        this.organogram = null;
      }
    });
  }

  uploadPassport(event: Event): void {
    const file = this.getFile(event);
    if (!file || !this.staff) {
      return;
    }

    this.processing = true;
    this.apiService.uploadHrStaffPassport(this.staff.ref, file).subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.processing = false;
      if (data.success === true) {
        this.notifyService.showSuccess('Passport photograph uploaded', 'HR & People');
        this.getStaff(this.staff.ref);
      } else {
        this.showApiError(data, 'Unable to upload passport');
      }
    });
  }

  uploadResume(event: Event): void {
    const file = this.getFile(event);
    if (!file || !this.staff) {
      return;
    }

    this.processing = true;
    this.apiService.uploadHrStaffResume(this.staff.ref, file).subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.processing = false;
      if (data.success === true) {
        this.notifyService.showSuccess('Resume uploaded', 'HR & People');
        this.getStaff(this.staff.ref);
      } else {
        this.showApiError(data, 'Unable to upload resume');
      }
    });
  }

  staffName(staff: HrStaffData): string {
    if (!staff) {
      return '';
    }

    return staff['name'] || [staff.firstName, staff.lastName].filter(Boolean).join(' ') || staff.email || 'Staff';
  }

  staffDesignation(staff: any): string {
    return staff?.jobTitle || staff?.role || '';
  }

  reportRows(): any[] {
    return this.organogram?.directReports || [];
  }

  displayDepartment(): string {
    const department: any = this.organogram?.department || this.staff?.department;
    return department?.title || department?.name || this.displayValue(department);
  }

  canManageHrStaff(): boolean {
    return this.checkService.canWrite(['hr.staff', 'users']) || this.checkService.canModify(['hr.staff', 'users']);
  }

  isOwnStaffProfile(): boolean {
    return Number(this.staff?.admin?.ref || 0) > 0 && Number(this.staff?.admin?.ref || 0) === Number(this.currentUser?.ref || 0);
  }

  canViewFullProfile(): boolean {
    return this.canManageHrStaff() || this.isOwnStaffProfile();
  }

  documentByType(type: string): any {
    if (!this.staff || !Array.isArray(this.staff.documents)) {
      return null;
    }

    return this.staff.documents.find(document => String(document.documentType || '').toLowerCase() === type.toLowerCase()) || null;
  }

  passportUrl(): string {
    const passport = this.documentByType('passport');
    return this.documentUrl(passport);
  }

  documentUrl(document: any): string {
    const filePath = String(document?.filePath || '');
    return /^https?:\/\//i.test(filePath) ? filePath : '';
  }

  placeholderUrl(): string {
    return 'https://ui-avatars.com/api/?name=' + encodeURIComponent(this.staffName(this.staff)) + '&background=0D8ABC&color=fff&size=256';
  }

  displayValue(value: any): string {
    return value === null || value === undefined || value === '' ? '-' : String(value);
  }

  private getFile(event: Event): File {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return null;
    }

    return input.files[0];
  }

  private showApiError(data: any, fallback: string): void {
    const message = data?.error?.additional_message ? data.error.message + '. ' + data.error.additional_message : data?.error?.message || data?.message || fallback;
    this.notifyService.showError(message, 'Error');
  }
}
